// Space Hub (Phase 54 Plan 06, Slice 3 — SPACE-UX-HUB).
//
// Center-detail hub at /spaces/:id. Ties together the four space-related
// surfaces that grew independently (Spaces list, Inventory > Space, Space
// Designer, Accounts) into one drill-down page with 5 tabs. Per CONTEXT.md's
// locked decision, this does NOT embed or replace the underlying
// /inventory/space/* routes — the non-Overview tabs are deep-link buttons to
// those same routes. Two entry points (sidebar-first and hub-first) are
// intentional.
import { useMemo } from "react";
import { useRoute, Link } from "wouter";
import { useListSpaces, useListSpaceFloors } from "@workspace/api-client-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { SpacePlansInline } from "@/components/spaces/space-plans-inline";

export default function SpaceHubPage() {
  const [, params] = useRoute("/spaces/:id");
  const id = params?.id ? Number(params.id) : NaN;

  const { data: spaces = [], isLoading: spacesLoading } = useListSpaces();
  // No spaceId filter exists server-side for /api/space/floors (it returns
  // every floor for the operator's accessible spaces) — filter client-side,
  // same pattern SpacePlansInline already uses for plans.
  const { data: floors = [] } = useListSpaceFloors();

  const space = useMemo(() => spaces.find((s) => s.id === id), [spaces, id]);
  const floorsForSpace = useMemo(
    () => floors.filter((f) => f.spaceId === id),
    [floors, id],
  );

  if (!Number.isFinite(id)) {
    return <div className="p-6 text-sm text-muted-foreground">Center not found.</div>;
  }

  if (spacesLoading) {
    return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;
  }

  if (!space) {
    return <div className="p-6 text-sm text-muted-foreground">Center not found.</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Link href="/spaces" className="hover:underline">Spaces</Link>
        <span>›</span>
        <span className="text-foreground">{space.name}</span>
      </div>
      <h1 className="text-3xl font-bold tracking-tight">{space.name}</h1>

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="floor-plans">Floor plans</TabsTrigger>
          <TabsTrigger value="inventory">Inventory</TabsTrigger>
          <TabsTrigger value="occupancy">Occupancy</TabsTrigger>
          <TabsTrigger value="accounts">Accounts</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-3">
          <Card>
            <CardHeader><CardTitle>Center details</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-muted-foreground">Name</span>
                <div>{space.name}</div>
              </div>
              <div>
                <span className="text-muted-foreground">City</span>
                <div>{space.city}</div>
              </div>
              <div>
                <span className="text-muted-foreground">GSTIN</span>
                <div className="font-mono">{space.gstin ?? "—"}</div>
              </div>
              <div>
                <span className="text-muted-foreground">Floors</span>
                <div>{floorsForSpace.length}</div>
              </div>
              <div className="col-span-2">
                <span className="text-muted-foreground">Registered Address</span>
                <div>{space.registeredAddress ?? "—"}</div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle>Plans</CardTitle></CardHeader>
            <CardContent className="p-0">
              <SpacePlansInline spaceId={id} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="floor-plans">
          <Card>
            <CardHeader><CardTitle>Floor plans</CardTitle></CardHeader>
            <CardContent className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                {floorsForSpace.length} floor{floorsForSpace.length === 1 ? "" : "s"} defined. Edit layout in Space Designer.
              </div>
              <Link href="/inventory/space/designer">
                <Button>Open Designer →</Button>
              </Link>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="inventory">
          <Card>
            <CardHeader><CardTitle>Inventory / Resources</CardTitle></CardHeader>
            <CardContent className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                Physical resources for this center (read-only — add via Space Designer).
              </div>
              <Link href="/inventory/space/resources">
                <Button>Open Resources →</Button>
              </Link>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="occupancy">
          <Card>
            <CardHeader><CardTitle>Occupancy</CardTitle></CardHeader>
            <CardContent className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                Real-time seat status (read-only — assign from Accounts / Add Plan).
              </div>
              <Link href="/inventory/space/occupancy">
                <Button>Open Occupancy →</Button>
              </Link>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="accounts">
          <Card>
            <CardHeader><CardTitle>Accounts in this center</CardTitle></CardHeader>
            <CardContent className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                Filtered list — accounts assigned to {space.name}.
              </div>
              <Link href={`/accounts?spaceId=${id}`}>
                <Button>Open filtered Accounts →</Button>
              </Link>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
